/**
 * Trusted devices (`core-api` §4.2, §5).
 *
 * A child's tablet is what says who the child is, so enrolling and revoking a
 * device is the strongest control a parent has here — stronger than a password
 * they do not have. Unlocking a PIN after too many attempts also lives here:
 * the lockout is deliberate, and lifting it is deliberate too.
 */

import { useCallback, useEffect, useState } from "react";

import { call } from "@/api/client";
import { ApiError } from "@/api/errors";
import type { ChildSummary, DeviceOut } from "@/api/schema";
import { Button, Card, Empty, ErrorNote, Loading, Screen } from "@/ui/primitives";

export function DevicesScreen() {
  const [children, setChildren] = useState<ChildSummary[] | null>(null);
  const [devices, setDevices] = useState<Record<string, DeviceOut[]>>({});
  const [error, setError] = useState<string | null>(null);
  const [label, setLabel] = useState("");

  const load = useCallback(async () => {
    try {
      const list = await call("listChildren");
      setChildren(list);
      const entries = await Promise.all(
        list.map(async (child) => [
          child.child_id,
          await call("listDevices", { params: { child_id: child.child_id } }),
        ] as const),
      );
      setDevices(Object.fromEntries(entries));
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : "Chargement impossible.");
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) return <ErrorNote>{error}</ErrorNote>;
  if (!children) return <Loading />;

  return (
    <Screen title="Appareils de confiance">
      <p className="notice">
        C'est l'appareil qui dit de quel enfant il s'agit. Un appareil révoqué ne peut plus se
        connecter, même avec le bon code.
      </p>

      {children.map((child) => (
        <Card key={child.child_id} title={child.display_name}>
          {(devices[child.child_id] ?? []).length === 0 ? (
            <Empty>Aucun appareil enrôlé.</Empty>
          ) : (
            <ul className="list">
              {(devices[child.child_id] ?? []).map((device) => (
                <li key={device.id} className="list__item">
                  <div>
                    <strong>{device.label}</strong>
                    <p className="muted small">
                      {device.revoked_at
                        ? "révoqué"
                        : `vu ${device.last_seen_at ? new Date(device.last_seen_at).toLocaleString("fr-FR") : "jamais"}`}
                    </p>
                  </div>
                  <div className="list__actions">
                    {!device.revoked_at ? (
                      <Button
                        variant="danger"
                        onClick={() => void call("revokeDevice", { params: { device_id: device.id } }).then(load)}
                      >
                        Révoquer
                      </Button>
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>
          )}

          <form
            className="inline-form"
            onSubmit={(event) => {
              event.preventDefault();
              if (!label.trim()) return;
              void call("enrolDevice", {
                params: { child_id: child.child_id },
                body: { label: label.trim() },
              })
                .then(load)
                .finally(() => setLabel(""));
            }}
          >
            <label className="sr-only" htmlFor={`label-${child.child_id}`}>
              Nom de l'appareil
            </label>
            <input
              id={`label-${child.child_id}`}
              className="input"
              value={label}
              onChange={(event) => setLabel(event.target.value)}
              placeholder="Tablette de la cuisine"
            />
            <Button type="submit" variant="secondary">
              Enrôler cet appareil
            </Button>
          </form>
          <p className="muted small">
            À faire depuis l'appareil de l'enfant : c'est ce navigateur-là qui reçoit le cookie
            d'appareil.
          </p>
        </Card>
      ))}
    </Screen>
  );
}
